'use client';

/**
 * TracingProvider
 *
 * React provider for browser-side OpenTelemetry tracing.
 * Initializes the OpenTelemetry SDK and provides hooks for manual instrumentation.
 *
 * Features:
 * - Dynamic imports to minimize bundle impact (~40KB gzipped, only when enabled)
 * - Auto-instrumentation: fetch, XHR, document load, route changes
 * - PII redaction matching backend patterns
 * - W3C TraceContext header propagation for end-to-end tracing
 *
 * @see Issue #469 - Frontend browser-side tracing
 */

import {
  type BrowserTracingConfig,
  type SpanAttributes,
  type SpanHandle,
  type TraceContext,
  type TracingContextValue,
  type TracingProviderProps,
} from '@ops-matrix/shared-types/observability';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// ============================================================================
// Types for OpenTelemetry (dynamically imported)
// ============================================================================

// These types match the OTel SDK but are declared here to avoid import-time dependencies
interface OTelSpan {
  setAttribute(key: string, value: string | number | boolean): void;
  recordException(error: Error): void;
  setStatus(status: { code: number; message?: string }): void;
  end(): void;
  spanContext(): { traceId: string; spanId: string; traceFlags?: number };
}

interface OTelTracer {
  startSpan(name: string, options?: { attributes?: Record<string, unknown> }): OTelSpan;
}

interface OTelProvider {
  getTracer(name: string): OTelTracer;
  shutdown(): Promise<void>;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_REDACT_FIELDS_INTERNAL = [
  'password',
  'token',
  'secret',
  'apiKey',
  'api_key',
  'authorization',
  'creditCard',
  'credit_card',
  'ssn',
  'socialSecurityNumber',
  'social_security_number',
  'email',
];

const DEFAULT_CONFIG: Partial<BrowserTracingConfig> = {
  enabled: true,
  sampleRate: 0.1,
  autoInstrumentation: true,
  userInteractionInstrumentation: false,
  routeInstrumentation: true,
  maxUrlLength: 256,
  redactFields: DEFAULT_REDACT_FIELDS_INTERNAL,
  ignoreUrls: [/\/health/, /\/metrics/, /google-analytics\.com/, /googletagmanager\.com/, /segment\.io/],
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Redact sensitive query parameters from URL
 */
function redactUrl(url: string, redactFields: string[], maxLength: number): string {
  try {
    const urlObj = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    for (const key of urlObj.searchParams.keys()) {
      if (redactFields.some((f) => key.toLowerCase().includes(f.toLowerCase()))) {
        urlObj.searchParams.set(key, '[REDACTED]');
      }
    }
    const result = urlObj.toString();
    return result.length > maxLength ? `${result.substring(0, maxLength)}...` : result;
  } catch {
    return url.length > maxLength ? `${url.substring(0, maxLength)}...` : url;
  }
}

// ============================================================================
// No-op implementations for when tracing is disabled
// ============================================================================

const noOpSpanHandle: SpanHandle = {
  setAttribute: () => {},
  setAttributes: () => {},
  recordError: () => {},
  end: () => {},
};

const noOpContextValue: TracingContextValue = {
  isEnabled: false,
  getTraceContext: () => null,
  getTraceHeaders: () => ({}),
  startSpan: () => noOpSpanHandle,
  recordInteraction: () => {},
  recordError: () => {},
};

// ============================================================================
// Context
// ============================================================================

const TracingContext = createContext<TracingContextValue>(noOpContextValue);

// ============================================================================
// Provider Component
// ============================================================================

export const TracingProvider: React.FC<TracingProviderProps> = ({ children, config, onTraceContextChange }) => {
  const providerRef = useRef<OTelProvider | null>(null);
  const tracerRef = useRef<OTelTracer | null>(null);
  const contextApiRef = useRef<{
    trace: { getActiveSpan(): OTelSpan | undefined };
    context: { active(): unknown };
    propagation: { inject(context: unknown, carrier: Record<string, string>): void };
  } | null>(null);

  const [isInitialized, setIsInitialized] = useState(false);

  const mergedConfig = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...config,
      redactFields: [...DEFAULT_REDACT_FIELDS_INTERNAL, ...(config.redactFields || [])],
    }),
    [config],
  );

  // Initialize OpenTelemetry SDK
  useEffect(() => {
    if (!mergedConfig.enabled || typeof window === 'undefined') {
      return;
    }

    let mounted = true;

    const initTracing = async () => {
      try {
        // Dynamic imports to reduce initial bundle size
        const [
          { WebTracerProvider, BatchSpanProcessor },
          { OTLPTraceExporter },
          { ZoneContextManager },
          { registerInstrumentations },
          { getWebAutoInstrumentations },
          { resourceFromAttributes },
          semanticConventions,
          otelApi,
        ] = await Promise.all([
          import('@opentelemetry/sdk-trace-web'),
          import('@opentelemetry/exporter-trace-otlp-http'),
          import('@opentelemetry/context-zone'),
          import('@opentelemetry/instrumentation'),
          import('@opentelemetry/auto-instrumentations-web'),
          import('@opentelemetry/resources'),
          import('@opentelemetry/semantic-conventions'),
          import('@opentelemetry/api'),
        ]);

        if (!mounted) return;

        // Create resource with service metadata
        const resource = resourceFromAttributes({
          [semanticConventions.ATTR_SERVICE_NAME]: mergedConfig.serviceName,
          [semanticConventions.ATTR_SERVICE_VERSION]: mergedConfig.serviceVersion || '0.0.1',
          'deployment.environment': (import.meta as { env?: { MODE?: string } }).env?.MODE || 'development',
          'service.namespace': 'ops-matrix',
          ...mergedConfig.resourceAttributes,
        });

        // Create OTLP exporter
        const exporter = new OTLPTraceExporter({
          url: mergedConfig.collectorUrl || 'http://localhost:4318/v1/traces',
          headers: {},
        });

        // Create provider with batch processor
        const spanProcessor = new BatchSpanProcessor(exporter, {
          maxQueueSize: 100,
          maxExportBatchSize: 30,
          scheduledDelayMillis: 5000,
        });

        const provider = new WebTracerProvider({
          resource,
          spanProcessors: [spanProcessor],
        });

        // Register zone context manager for async context propagation
        provider.register({
          contextManager: new ZoneContextManager(),
        });

        // Register auto-instrumentations
        if (mergedConfig.autoInstrumentation) {
          const propagateOrigins = mergedConfig.propagateTraceHeaderOrigins || [/.*/];

          registerInstrumentations({
            instrumentations: [
              getWebAutoInstrumentations({
                '@opentelemetry/instrumentation-fetch': {
                  enabled: true,
                  propagateTraceHeaderCorsUrls: propagateOrigins,
                  clearTimingResources: true,
                  applyCustomAttributesOnSpan: (span: OTelSpan, request: unknown) => {
                    if (request && typeof request === 'object' && 'url' in request) {
                      const req = request as { url: string };
                      span.setAttribute(
                        'browser.url',
                        redactUrl(req.url, mergedConfig.redactFields!, mergedConfig.maxUrlLength!),
                      );
                    }
                  },
                },
                '@opentelemetry/instrumentation-xml-http-request': {
                  enabled: true,
                  propagateTraceHeaderCorsUrls: propagateOrigins,
                  clearTimingResources: true,
                },
                '@opentelemetry/instrumentation-document-load': {
                  enabled: true,
                },
                '@opentelemetry/instrumentation-user-interaction': {
                  enabled: mergedConfig.userInteractionInstrumentation,
                },
              }),
            ],
          });
        }

        providerRef.current = provider as unknown as OTelProvider;
        tracerRef.current = provider.getTracer(mergedConfig.serviceName) as unknown as OTelTracer;
        contextApiRef.current = otelApi as typeof contextApiRef.current;

        setIsInitialized(true);

        // eslint-disable-next-line no-console
        console.log(`[OTel] Browser tracing initialized for ${mergedConfig.serviceName}`);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[OTel] Failed to initialize browser tracing:', error);
      }
    };

    initTracing();

    // Cleanup on unmount
    return () => {
      mounted = false;
      providerRef.current?.shutdown().catch(() => {});
    };
  }, [mergedConfig]);

  // ============================================================================
  // Context Value Methods
  // ============================================================================

  const getTraceContext = useCallback((): TraceContext | null => {
    if (!contextApiRef.current) return null;

    const activeSpan = contextApiRef.current.trace.getActiveSpan();
    if (!activeSpan) return null;

    const spanContext = activeSpan.spanContext();
    return {
      traceId: spanContext.traceId,
      spanId: spanContext.spanId,
      traceFlags: spanContext.traceFlags,
    };
  }, []);

  const getTraceHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {};

    if (!contextApiRef.current) return headers;

    const activeContext = contextApiRef.current.context.active();
    contextApiRef.current.propagation.inject(activeContext, headers);

    return headers;
  }, []);

  const startSpan = useCallback((name: string, attributes?: SpanAttributes): SpanHandle => {
    if (!tracerRef.current) {
      return noOpSpanHandle;
    }

    const span = tracerRef.current.startSpan(name, { attributes });

    return {
      setAttribute: (key, value) => span.setAttribute(key, value),
      setAttributes: (attrs) => {
        Object.entries(attrs).forEach(([k, v]) => {
          if (v !== undefined) span.setAttribute(k, v as string | number | boolean);
        });
      },
      recordError: (error) => {
        span.recordException(error);
        span.setStatus({ code: 2, message: error.message }); // SpanStatusCode.ERROR = 2
      },
      end: () => span.end(),
    };
  }, []);

  const recordInteraction = useCallback(
    (action: string, target?: string, attributes?: SpanAttributes) => {
      const span = startSpan(`user.${action}`, {
        'browser.action': action,
        'browser.target': target,
        ...attributes,
      });
      span.end();
    },
    [startSpan],
  );

  const recordError = useCallback(
    (error: Error, attributes?: SpanAttributes) => {
      const span = startSpan('error', {
        'error.type': error.name,
        'error.message': error.message,
        ...attributes,
      });
      span.recordError(error);
      span.end();
    },
    [startSpan],
  );

  // Notify when trace context changes
  useEffect(() => {
    if (onTraceContextChange && isInitialized) {
      const context = getTraceContext();
      onTraceContextChange(context);
    }
  }, [isInitialized, onTraceContextChange, getTraceContext]);

  // ============================================================================
  // Global Error Capturing
  // ============================================================================

  useEffect(() => {
    if (!isInitialized || typeof window === 'undefined') return;

    // Capture unhandled JavaScript errors
    const handleError = (event: ErrorEvent) => {
      const error = event.error || new Error(event.message);
      recordError(error, {
        'error.source': 'window.onerror',
        'error.filename': event.filename || 'unknown',
        'error.lineno': event.lineno || 0,
        'error.colno': event.colno || 0,
        'browser.url': window.location.href,
      });
    };

    // Capture unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      recordError(error, {
        'error.source': 'unhandledrejection',
        'error.type': 'UnhandledPromiseRejection',
        'browser.url': window.location.href,
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [isInitialized, recordError]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const contextValue = useMemo<TracingContextValue>(
    () => ({
      isEnabled: mergedConfig.enabled ?? false,
      getTraceContext,
      getTraceHeaders,
      startSpan,
      recordInteraction,
      recordError,
    }),
    [mergedConfig.enabled, getTraceContext, getTraceHeaders, startSpan, recordInteraction, recordError],
  );

  return <TracingContext.Provider value={contextValue}>{children}</TracingContext.Provider>;
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Access tracing context. Returns no-op implementation if not in provider.
 *
 * Unlike useDesignSystem, this hook does NOT throw when used outside a provider.
 * This allows components to safely call tracing methods without requiring
 * the provider to be present (useful for shared components).
 */
export function useTracing(): TracingContextValue {
  return useContext(TracingContext);
}

/**
 * Access tracing context, returning null if not in provider.
 * Use this when you need to check if tracing is available.
 */
export function useTracingOptional(): TracingContextValue | null {
  const context = useContext(TracingContext);
  return context.isEnabled ? context : null;
}

/**
 * Hook to get trace headers for API calls.
 * Returns a function that can be called to get current headers.
 *
 * @example
 * ```tsx
 * const getHeaders = useTraceHeaders();
 *
 * const response = await fetch('/api/data', {
 *   headers: {
 *     ...getHeaders(),
 *     'Content-Type': 'application/json',
 *   },
 * });
 * ```
 */
export function useTraceHeaders(): () => Record<string, string> {
  const { getTraceHeaders } = useTracing();
  return getTraceHeaders;
}

// ============================================================================
// Error Boundary Component
// ============================================================================

interface TracingErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode | ((error: Error) => React.ReactNode);
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface TracingErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary that automatically reports errors to OpenTelemetry.
 * Use this to wrap sections of your app where you want to catch and trace errors.
 *
 * @example
 * ```tsx
 * <TracingErrorBoundary fallback={<ErrorPage />}>
 *   <MyComponent />
 * </TracingErrorBoundary>
 * ```
 */
class TracingErrorBoundaryClass extends React.Component<
  TracingErrorBoundaryProps & { recordError: (error: Error, attributes?: SpanAttributes) => void },
  TracingErrorBoundaryState
> {
  constructor(props: TracingErrorBoundaryProps & { recordError: (error: Error, attributes?: SpanAttributes) => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): TracingErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Record to OpenTelemetry
    this.props.recordError(error, {
      'error.source': 'react.error_boundary',
      'error.component_stack': errorInfo.componentStack || 'unknown',
      'browser.url': typeof window !== 'undefined' ? window.location.href : 'unknown',
    });

    // Call optional user callback
    this.props.onError?.(error, errorInfo);
  }

  override render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error);
      }
      return this.props.fallback ?? null;
    }

    return this.props.children;
  }
}

/**
 * Error boundary wrapper that connects to TracingContext.
 * Must be used inside a TracingProvider.
 */
export const TracingErrorBoundary: React.FC<TracingErrorBoundaryProps> = (props) => {
  const { recordError } = useTracing();
  return <TracingErrorBoundaryClass {...props} recordError={recordError} />;
};

// ============================================================================
// Exports
// ============================================================================

export type {
  BrowserTracingConfig,
  SpanHandle,
  TracingContextValue,
  TracingProviderProps,
} from '@ops-matrix/shared-types/observability';
export { TracingContext };

export default TracingProvider;
